import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Search, UserX } from "lucide-react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { api, extractApiError } from "../lib/api";
import { useSession } from "../lib/auth";
import { fetchAdminUsers } from "../lib/dataFetchers";
import type { AdminUserFixture } from "../lib/fixtures";
import { ROLES, ROLE_LABEL, type Role } from "../lib/permissions";
import { cn } from "../lib/cn";
import { FilterBar, FilterGroup } from "../components/ui/FilterBar";
import { Select } from "../components/ui/Select";
import { Button } from "../components/ui/Button";
import { InvitationsList } from "../components/admin/InvitationsList";
import { RevokeUserConfirmDialog } from "../components/admin/RevokeUserConfirmDialog";

export default function Admin(): JSX.Element {
  const qc = useQueryClient();
  const { user: currentUser, refresh: refreshSession } = useSession();
  const { data: users, isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: fetchAdminUsers,
    refetchInterval: 30_000,
  });

  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState<Role | "ALL">("ALL");
  // null = no revoke dialog open; non-null = which user the admin is about to revoke
  const [revoking, setRevoking] = useState<AdminUserFixture | null>(null);

  const { t } = useTranslation(["admin", "common"]);

  // Revoked (soft-deleted) users stay in the LIST with a "Revoked" badge, but
  // are excluded from the KPI head-counts so the numbers reflect who actually
  // has access right now.
  const activeUsers = useMemo(
    () => (users ?? []).filter((u) => !u.deletedAt),
    [users],
  );

  // Active-admin count drives the "hide Revoke button for last admin" rule
  // on the frontend. The backend ALSO rejects with 409 — this is a UX guard,
  // not a security boundary.
  const activeAdminCount = useMemo(
    () => activeUsers.filter((u) => u.role === "admin").length,
    [activeUsers],
  );

  const filteredUsers = useMemo(() => {
    const list = users ?? [];
    const needle = q.trim().toLowerCase();
    return list.filter((u) => {
      if (roleFilter !== "ALL" && u.role !== roleFilter) return false;
      if (needle) {
        const blob = `${u.name ?? ""} ${u.email ?? ""}`.toLowerCase();
        if (!blob.includes(needle)) return false;
      }
      return true;
    });
  }, [users, q, roleFilter]);

  const mutation = useMutation({
    mutationFn: async (vars: { id: string; role: Role }) => {
      const res = await api.patch(`/admin/users/${vars.id}`, { role: vars.role });
      return res.data;
    },
    onSuccess: async (_data, vars) => {
      toast.success(t("admin:role_updated"));
      void qc.invalidateQueries({ queryKey: ["admin-users"] });
      // If the admin somehow changed their own row (this should be blocked
      // by both the backend and the UI guard below — kept as defence in depth
      // so the session never drifts from the DB role), refresh the session so
      // ProtectedRoute re-evaluates against the new role on the next render.
      if (currentUser?.id && vars.id === currentUser.id) {
        await refreshSession();
      }
    },
    onError: (err) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const msg = extractApiError(err);
      if (status === 409 && /cannot_demote_last_admin/.test(msg)) {
        toast.error(t("admin:cannot_demote_last_admin"));
      } else if (status === 400 && /cannot_change_own_role/.test(msg)) {
        toast.error(
          t("admin:cannot_change_own_role", {
            defaultValue: "You cannot change your own role.",
          }),
        );
      } else if (status === 409 && /user_revoked/.test(msg)) {
        toast.error(
          t("admin:cannot_change_revoked_role", {
            defaultValue: "Cannot change role for a revoked user.",
          }),
        );
      } else {
        toast.error(msg);
      }
    },
  });

  const roleSelectOptions = useMemo(
    () => [
      { value: "ALL", label: t("common:all_roles") },
      ...ROLES.map((r) => ({ value: r, label: ROLE_LABEL[r] })),
    ],
    [t],
  );

  return (
    <div className="max-w-5xl mx-auto">
      {/* Page header */}
      <div className="flex items-end justify-between gap-3 flex-wrap mb-[18px]">
        <div>
          <h1 className="text-[24px] leading-[1.1] font-display font-semibold text-text">
            {t("admin:page_title")}
          </h1>
          <p className="text-[13px] text-muted mt-[5px]">
            {t("admin:page_subtitle")}
          </p>
        </div>
      </div>

      {/* KPI cards — proto .kpi look */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-[13px] mb-[18px]">
        <KpiCard
          label={t("admin:stat_users")}
          value={activeUsers.length}
          borderColor="var(--accent)"
        />
        <KpiCard
          label={t("admin:stat_admins")}
          value={activeUsers.filter((u) => u.role === "admin").length}
          borderColor="var(--amber)"
        />
        <KpiCard
          label={t("admin:stat_legal_advisors")}
          value={activeUsers.filter((u) => u.role === "legal_advisor").length}
          borderColor="var(--green)"
        />
        <KpiCard
          label={t("admin:stat_members")}
          value={activeUsers.filter((u) => u.role === "user").length}
          borderColor="var(--dim)"
        />
      </div>

      {/* Invitations card */}
      <div className="mb-[18px]">
        <InvitationsList />
      </div>

      {/* Filters */}
      <FilterBar>
        <FilterGroup label={t("common:search")} className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search
              className="absolute left-[11px] top-1/2 -translate-y-1/2 h-4 w-4 text-dim"
              aria-hidden
            />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              type="search"
              placeholder={t("common:search_placeholder_users")}
              aria-label={t("common:search")}
              className="w-full h-9 pl-[35px] pr-3 border border-border rounded-lg bg-surface-2 text-[13.5px] text-text focus:bg-surface focus:border-accent focus:ring-[3px] focus:ring-accent/[.14] focus:outline-none"
            />
          </div>
        </FilterGroup>
        <FilterGroup label={t("admin:col_role")}>
          <Select
            value={roleFilter}
            onValueChange={(v) => setRoleFilter(v as Role | "ALL")}
            options={roleSelectOptions}
            placeholder={t("common:all_roles")}
            minWidth="min-w-[160px]"
          />
        </FilterGroup>
      </FilterBar>

      {/* Users table / mobile cards */}
      <div className="bg-surface border border-border rounded-[11px] shadow-card overflow-hidden">
        {isLoading ? (
          <div className="p-[15px] space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="skeleton h-12" />
            ))}
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-[13px] text-muted">{t("admin:no_match")}</p>
            <Button
              variant="secondary"
              className="mt-3"
              onClick={() => {
                setQ("");
                setRoleFilter("ALL");
              }}
            >
              {t("common:clear_filters")}
            </Button>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <table className="hidden sm:table w-full border-collapse">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-[11px] font-bold tracking-[.04em] uppercase text-dim px-[15px] py-[11px] bg-surface sticky top-0 z-[1]">
                    {t("admin:col_name")}
                  </th>
                  <th className="text-left text-[11px] font-bold tracking-[.04em] uppercase text-dim px-[15px] py-[11px] bg-surface sticky top-0 z-[1]">
                    {t("admin:col_email")}
                  </th>
                  <th className="text-left text-[11px] font-bold tracking-[.04em] uppercase text-dim px-[15px] py-[11px] bg-surface sticky top-0 z-[1]">
                    {t("admin:col_role")}
                  </th>
                  <th className="text-left text-[11px] font-bold tracking-[.04em] uppercase text-dim px-[15px] py-[11px] bg-surface sticky top-0 z-[1]">
                    {t("admin:col_joined")}
                  </th>
                  <th className="text-right text-[11px] font-bold tracking-[.04em] uppercase text-dim px-[15px] py-[11px] bg-surface sticky top-0 z-[1] w-[1%]">
                    {t("admin:col_actions", { defaultValue: "Actions" })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => {
                  const isRevoked = !!u.deletedAt;
                  const isSelf =
                    currentUser?.email && u.email
                      ? currentUser.email.toLowerCase() === u.email.toLowerCase()
                      : false;
                  const isLastAdmin =
                    u.role === "admin" && !isRevoked && activeAdminCount === 1;
                  const showRevoke = !isRevoked && !isSelf && !isLastAdmin;
                  return (
                    <tr
                      key={u.id}
                      className={cn(
                        "border-b border-border-2 last:border-b-0 hover:bg-surface-2 transition-colors",
                        isRevoked && "opacity-60",
                      )}
                    >
                      <td className="px-[15px] py-[13px] text-[13.5px] font-semibold text-text">
                        {u.name ?? "—"}
                        {isRevoked && (
                          <span
                            className="ml-2 inline-block px-1.5 py-0.5 rounded-md bg-red-soft text-red-text text-[10px] font-semibold uppercase tracking-[.04em] align-middle"
                            aria-label={t("admin:revoked_badge", {
                              defaultValue: "Access revoked",
                            })}
                          >
                            {t("admin:status_revoked", { defaultValue: "Revoked" })}
                          </span>
                        )}
                      </td>
                      <td className="px-[15px] py-[13px] text-[13.5px] text-muted">
                        {u.email}
                      </td>
                      <td className="px-[15px] py-[13px]">
                        {isSelf ? (
                          <div
                            className="inline-flex items-center gap-2 text-[13px] text-text"
                            aria-label={t("admin:role_self_readonly", {
                              defaultValue:
                                "Your own role can only be changed by another admin.",
                            })}
                          >
                            <span className="font-medium">{ROLE_LABEL[u.role]}</span>
                            <span
                              className="inline-block px-1.5 py-0.5 rounded-md bg-surface-2 text-dim text-[10px] font-semibold uppercase tracking-[.04em]"
                            >
                              {t("admin:you_badge", { defaultValue: "You" })}
                            </span>
                          </div>
                        ) : (
                          <RoleSelect
                            value={u.role}
                            onChange={(role) => mutation.mutate({ id: u.id, role })}
                            disabled={mutation.isPending || isRevoked}
                          />
                        )}
                      </td>
                      <td className="px-[15px] py-[13px] text-[13.5px] text-dim tabular-nums">
                        {new Date(u.createdAt).toLocaleDateString(i18n.language)}
                      </td>
                      <td className="px-[15px] py-[13px] text-right">
                        {showRevoke && (
                          <Button
                            type="button"
                            variant="danger-soft"
                            size="xs"
                            onClick={() => setRevoking(u)}
                            aria-label={t("admin:revoke_access_for", {
                              defaultValue: "Revoke access for {{name}}",
                              name: u.name ?? u.email,
                            })}
                          >
                            <UserX className="h-3.5 w-3.5" aria-hidden />
                            {t("admin:revoke", { defaultValue: "Revoke" })}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile card list */}
            <ul className="sm:hidden divide-y divide-border-2">
              {filteredUsers.map((u) => {
                const isRevoked = !!u.deletedAt;
                const isSelf =
                  currentUser?.email && u.email
                    ? currentUser.email.toLowerCase() === u.email.toLowerCase()
                    : false;
                const isLastAdmin =
                  u.role === "admin" && !isRevoked && activeAdminCount === 1;
                const showRevoke = !isRevoked && !isSelf && !isLastAdmin;
                return (
                  <li
                    key={u.id}
                    className={cn("p-[14px_15px]", isRevoked && "opacity-60")}
                  >
                    <div className="font-semibold text-[13.5px] text-text">
                      {u.name ?? "—"}
                      {isRevoked && (
                        <span className="ml-2 inline-block px-1.5 py-0.5 rounded-md bg-red-soft text-red-text text-[10px] font-semibold uppercase tracking-[.04em] align-middle">
                          {t("admin:status_revoked", { defaultValue: "Revoked" })}
                        </span>
                      )}
                    </div>
                    <div className="text-[12px] text-muted mt-0.5">{u.email}</div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {isSelf ? (
                        <div
                          className="inline-flex items-center gap-2 text-[13px] text-text"
                          aria-label={t("admin:role_self_readonly", {
                            defaultValue:
                              "Your own role can only be changed by another admin.",
                          })}
                        >
                          <span className="font-medium">{ROLE_LABEL[u.role]}</span>
                          <span className="inline-block px-1.5 py-0.5 rounded-md bg-surface-2 text-dim text-[10px] font-semibold uppercase tracking-[.04em]">
                            {t("admin:you_badge", { defaultValue: "You" })}
                          </span>
                        </div>
                      ) : (
                        <RoleSelect
                          value={u.role}
                          onChange={(role) => mutation.mutate({ id: u.id, role })}
                          disabled={mutation.isPending || isRevoked}
                        />
                      )}
                      {showRevoke && (
                        <Button
                          type="button"
                          variant="danger-soft"
                          size="xs"
                          onClick={() => setRevoking(u)}
                        >
                          <UserX className="h-3.5 w-3.5" aria-hidden />
                          {t("admin:revoke", { defaultValue: "Revoke" })}
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      {/* Revoke confirmation dialog — opened by the Revoke button on each row */}
      {revoking && (
        <RevokeUserConfirmDialog
          userId={revoking.id}
          userEmail={revoking.email}
          userName={revoking.name}
          open={!!revoking}
          onClose={() => setRevoking(null)}
          onRevoked={() => setRevoking(null)}
        />
      )}
    </div>
  );
}

// ── KPI card ──────────────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  borderColor,
}: {
  label: string;
  value: number;
  borderColor: string;
}): JSX.Element {
  return (
    <div
      className="bg-surface border border-border rounded-[11px] shadow-card p-[14px_16px]"
      style={{ borderLeftWidth: 4, borderLeftColor: borderColor }}
    >
      <div className="text-[11px] font-bold tracking-[.03em] uppercase text-muted">
        {label}
      </div>
      <div className="font-display text-[27px] font-semibold mt-[5px] tabular-nums">
        {value}
      </div>
    </div>
  );
}

// ── Role select ───────────────────────────────────────────────────────────────

function RoleSelect({
  value,
  onChange,
  disabled,
}: {
  value: Role;
  onChange: (r: Role) => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as Role)}
      disabled={disabled}
      aria-label="Change role"
      className={cn(
        "h-9 px-3 rounded-lg border border-border bg-surface text-[13px] text-text",
        "focus:bg-surface focus:border-accent focus:ring-[3px] focus:ring-accent/[.14] focus:outline-none",
        "disabled:opacity-50",
        "min-h-[44px] sm:min-h-[36px]",
      )}
    >
      {ROLES.map((r) => (
        <option key={r} value={r}>
          {ROLE_LABEL[r]}
        </option>
      ))}
    </select>
  );
}
